import { useEffect, useRef, useSyncExternalStore } from "react";
import { currentInstallKind, usePwa, type InstallKind } from "./pwa.ts";

/** What to offer this player about installing the app, kept current as the browser's prompt arrives. */
export function useInstallKind(): InstallKind {
  const installEvent = usePwa((s) => s.installEvent);
  const standalone = usePwa((s) => s.standalone);
  return currentInstallKind({ installEvent, standalone });
}

const subscribe = (notify: () => void) => {
  window.addEventListener("online", notify);
  window.addEventListener("offline", notify);
  return () => {
    window.removeEventListener("online", notify);
    window.removeEventListener("offline", notify);
  };
};

/** Whether the device thinks it has a connection. `false` is reliable; `true` only means "probably". */
export const useOnline = (): boolean =>
  useSyncExternalStore(
    subscribe,
    () => navigator.onLine,
    () => true,
  );

/** Run `retry` once, the moment the connection comes back, while `active` (e.g. while an error is showing). */
export function useRetryWhenOnline(active: boolean, retry: () => void): void {
  const latest = useRef(retry);
  latest.current = retry;
  useEffect(() => {
    if (!active) return;
    const on = () => latest.current();
    window.addEventListener("online", on, { once: true });
    return () => window.removeEventListener("online", on);
  }, [active]);
}
